import {API_URLS} from '@/const/urls';
import {http} from '@/helper/http';
import {sd} from '@/da/sd';
import {IP_RESTRICTED_CODE} from '@/emum';
import {popupUtil} from '@/helper/popup';

function encryptPassword(params) {
  params.encryptVersion = 0;
}

function ipRestrictedFail(res, LG) {
  if ([IP_RESTRICTED_CODE.RESTRICTED, IP_RESTRICTED_CODE.WARNING].includes(res.code)) {
    import('@/modules/register/popups/RegisterTips.svelte').then((RegisterTips) => {
      popupUtil.create(RegisterTips.default, {LG});
    });
  }
}

export const userApi = {
  // Email：发送邮件是高风险接口，增加了 waf 规则，需要兼容 202 状态码
  async publicCodesMailPost(query, params) {
    const res = await http.post(API_URLS.PUBLIC_CODES_MAIL, {query, params});
    const reply = res.reply || {};
    if (reply.status === 202 && reply.code === 498) {
      res.error = undefined;
      res.data = {};
    }
    return res;
  },
  async publicCodesMail(query) {
    const res = await http.post(API_URLS.PUBLIC_CODES_MAIL, {query});
    const reply = res.reply || {};
    if (reply.status === 202 && reply.code === 498) {
      res.error = undefined;
      res.data = {};
    }
    return res;
  },
  codesMail(query, params) {
    return http.post(API_URLS.CODES_MAIL, {query, params});
  },
  // Users
  getSelf() {
    return http.get(API_URLS.SELF, {
      formatter(data) {
        data.isMain = data.parentId === 0;
        data.isSub = !data.isMain;
        const {lang} = data;
        data.lang = ['zh', 'cn'].indexOf(lang) > -1 ? 'en' : lang;
        return data;
      },
    });
  },
  getUsersSelf() {
    return http.get(API_URLS.USERS_SELF, {
      formatter(data) {
        data.isMain = data.parentId === 0;
        data.isSub = !data.isMain;
        const {lang} = data;
        data.lang = ['zh', 'cn'].indexOf(lang) > -1 ? 'en' : lang;
        return data;
      },
    });
  },
  logout() {
    return http.post(API_URLS.USERS_LOGOUT, {});
  },
  addGroupClickCnt(group_id) {
    return http.post(API_URLS.ADD_GROUP_CLICK_CNT, {params: {id: group_id}});
  },
  login(query, params, LG) {
    const {email: origEmail, ...res} = params;
    const email = origEmail.trim();
    encryptPassword(params);
    return http.post(API_URLS.USER_LOGIN, {query, params: {...res, email}, fail: (args) => ipRestrictedFail(args, LG)});
  },
  crossSiteLogin(query, params, LG) {
    encryptPassword(params);
    return http.post(API_URLS.USER_LOGIN, {query, params, fail: (args) => ipRestrictedFail(args, LG)});
  },
  confirmLogin(params) {
    return http.post(API_URLS.USERS_CONFIRM_LOGIN, {params});
  },
  crossSiteConfirmLogin(query) {
    return http.get(API_URLS.CROSS_SITE_USERS_CONFIRM_LOGIN, {query});
  },
  signUp(query, params) {
    const {email: origEmail, ...res} = params;
    const email = origEmail.trim();
    encryptPassword(params);
    return http.post(API_URLS.USER_SIGNUP, {query, params: {...res, email}});
  },
  crossSiteSignUp(query, params) {
    encryptPassword(params);
    return http.post(API_URLS.CROSS_SITE_USER_SIGNUP, {query, params});
  },
  confirmRegister(params) {
    return http.post(API_URLS.USERS_CONFIRM_REGISTER, {params});
  },
  preparePassword(query) {
    return http.post(API_URLS.PREPARE_PASSWORD, {query});
  },
  confirmResetPassword(query, params) {
    encryptPassword(params);
    return http.post(API_URLS.CONFIRM_PASSWORD, {query, params});
  },
  getPreference() {
    return http.get(API_URLS.PREFERENCE, {});
  },
  updatePreference(params) {
    return http.post(API_URLS.PREFERENCE, {params});
  },
  getExchangeBonus() {
    return http.get(API_URLS.EXCHANGE_BONUS, {});
  },
  async getPublicGeoV2(query) {
    const res = await http.get(API_URLS.PUBLIC_GEO_V2, {query: query || {}});
    trackGeoData(res && res.data);
    return res;
  },
  updateExchangeBonus(params) {
    return http.post(API_URLS.EXCHANGE_BONUS, {params});
  },
  vipApply(params) {
    return http.post(API_URLS.VIP_APPLY, {params});
  },
  pubVipApply(params) {
    return http.post(API_URLS.PUB_VIP_APPLY, {params});
  },
  getVipConfig() {
    return http.get(API_URLS.VIP_CONFIG, {});
  },
  getDmmConfig() {
    return http.get(API_URLS.DMM_CONFIG, {});
  },
  getVipLevelList() {
    return http.get(API_URLS.VIP_LEVEL_LIST, {});
  },
  bigClientList() {
    return http.get(API_URLS.BIG_CLIENT_LIST, {});
  },
  bigClientSelf() {
    return http.get(API_URLS.BIG_CLIENT_SELF, {});
  },
  bigClientDownload() {
    return http.post(API_URLS.BIG_CLIENT_DOWNLOAD, {});
  },
  registerVerified(params) {
    return http.post(API_URLS.REGISTER_VERIFIED, {params});
  },
  loginWithToken(params) {
    return http.post(API_URLS.LOGIN_WITH_TOKEN, {params});
  },
  loadLocalIp() {
    return http.get(API_URLS.EXCHANGE_BONUS_IP, {});
  },
  confirmDelete(params) {
    return http.post(API_URLS.DELETE_ACCOUNT, {params});
  },
  bindGoogleClientId(params) {
    return http.post(API_URLS.GOOGLE_CLIENT_ID, {params});
  },
  getTag(query) {
    return http.get(API_URLS.USER_TAG, {query});
  },
  getUserDevices(query) {
    return http.get(API_URLS.USER_DEVICES, {query});
  },
  getUserDeviceHistory(query) {
    return http.get(API_URLS.USER_DEVICE_HISTORY, {query});
  },
  deleteUserDevice(params) {
    return http.delete(API_URLS.DELETE_USER_DEVICE, {params, headers: {'Content-Type': 'application/json'}});
  },
  getProfitEmailNotification() {
    return http.get(API_URLS.PROFIT_EMAIL_NOTIFICATION, {});
  },
  updateProfitEmailNotification(params) {
    return http.post(API_URLS.PROFIT_EMAIL_NOTIFICATION, {params});
  },
  getFavoriteList() {
    return http.get(API_URLS.FAVORITE_LIST, {});
  },
  addFavorite(params) {
    return http.post(API_URLS.ADD_FAVORITE, {params});
  },
  delFavorite(params) {
    return http.delete(API_URLS.DEL_FAVORITE, {params, headers: {'Content-Type': 'application/json'}});
  },
  getFavoriteMerge() {
    return http.get(API_URLS.FAVORITES_MERGE, {});
  },
  favoriteMerge(params) {
    return http.post(API_URLS.FAVORITES_MERGE, {params});
  },
  deleteApply() {
    return http.post(API_URLS.DELETE_APPLY, {});
  },
  deleteSaveAsset(params) {
    return http.post(API_URLS.DELETE_SAVE_ASSET, {params});
  },
  deleteConfirm(params) {
    return http.post(API_URLS.DELETE_CONFIRM, {params});
  },
  userSpotProfile(query) {
    return http.get(API_URLS.SPOT_PROFILE, {query});
  },
  getPNLChart(query) {
    return http.get(API_URLS.USER_PNL_CHART, {query});
  },
  getPNLRateChart(query) {
    return http.get(API_URLS.USER_PNL_RATE_CHART, {query});
  },
  getBaseKycCheck() {
    return http.get(API_URLS.BASE_KYC_CHECK, {});
  },
  getAvatarList() {
    return http.get(API_URLS.USER_AVATAR, {});
  },
  getUserCpfs() {
    return http.get(API_URLS.USER_CPFS, {});
  },
  updateUserCpfs(params) {
    return http.post(API_URLS.USER_CPFS, {params});
  },
  getUserMemberLevel() {
    return http.get(API_URLS.USER_MEMBER_LEVEL, {});
  },
  getUserContractGray() {
    return http.get(API_URLS.CONTRACT_GRAY, {});
  },
  getHashClientId() {
    return http.get(API_URLS.HASH_CLIENT_ID, {});
  },
  authLogin(query, params) {
    encryptPassword(params);
    return http.post(API_URLS.USERS_AUTH_LOGIN, {query, params});
  },
  confirmAuthLogin(params) {
    return http.post(API_URLS.CONFIRM_USERS_AUTH_LOGIN, {params});
  },
  getUserAnnualData() {
    return http.get(API_URLS.ANNUAL_DATA, {query: {}});
  },
  getUserAnnualRank() {
    return http.get(API_URLS.ANNUAL_RANK, {query: {}});
  },
  getCustomizeBiz(query) {
    return http.get(API_URLS.QUERY_CUSTOMIZE, {query});
  },
  updateCustomizeBiz(params) {
    return http.post(API_URLS.UPDATE_CUSTOMIZE, {params});
  },
  getGray() {
    return http.get(API_URLS.COMMON_GRAY, {});
  },
  getOauthAuthorize(params) {
    return http.post(API_URLS.OAUTH_TRADINGVIEW_AUTHORIZE, {params});
  },
  getAuthorize(query) {
    return http.get(API_URLS.OAUTH_LIST, {query});
  },
  getAuthInfo(query) {
    return http.get(API_URLS.OAUTH_INFO, {query});
  },
  deleteOauthAuthorize(query) {
    return http.post(API_URLS.OAUTH_INVALID, {query});
  },
  getApplyStatus() {
    return http.get(API_URLS.APPLY_STATUS, {});
  },
  getUserBizGray() {
    return http.get(API_URLS.ACCOUNT_BIZ_GRAY, {});
  },
  getUtaInfo() {
    return http.get(API_URLS.UTA_QUERY, {});
  },
  postUtaSpotUpgradeAnswerCompleted() {
    return http.post(API_URLS.UTA_SPOT_UPGRADE_ANSWER_COMPLETED, {});
  },
  postUtaSpotUpgradeApply() {
    return http.post(API_URLS.UTA_SPOT_UPGRADE_APPLY, {});
  },
  getUtaSpotUpgradeCheckSub() {
    return http.get(API_URLS.UTA_SPOT_UPGRADE_CHECK_SUB, {});
  },
  postUtaSpotUpgradeApplySub(query) {
    return http.post(API_URLS.UTA_SPOT_UPGRADE_APPLY_SUB, {query});
  },
  switchContractPtDiscount(query) {
    return http.post(API_URLS.CONTRACT_PT_DISCOUNT, {query});
  },
  switchSpotPtDiscount(params) {
    // params.walletType: SPOT，Margin, UTA
    // params.kickOff: 0：关闭，1：打开
    return http.post(API_URLS.SPOT_PT_DISCOUNT, {params});
  },
  getTwitterClientId() {
    return http.get(API_URLS.GET_TWITTER_CLIENT_ID, {query: {source: 'twitter'}});
  },
  getUserFallbackSocialAvatar(params) {
    return http.post(API_URLS.USER_SOCIAL_AVATAR, {params});
  },
  loginCheckPasskey(query) {
    return http.get(API_URLS.PASSKEY_CHECK, {query});
  },
  loginWithPasskey(query, params, LG) {
    const {email: origEmail, ...res} = params;
    const email = origEmail.trim();
    encryptPassword(params);
    return http.post(API_URLS.PASSKEY_LOGIN, {
      query,
      params: {...res, email},
      fail: (args) => {
        const error = args.length > 0 ? args[0] : {};
        if (error.code !== IP_RESTRICTED_CODE.RESTRICTED) {
          return Promise.resolve({error});
        }
        ipRestrictedFail(args, LG);
      },
    });
  },
  loginConfirmWithPasskey(params) {
    return http.post(API_URLS.PASSKEY_CONFIRM_LOGIN, {params});
  },
  getLoginQrcode(query) {
    return http.post(API_URLS.USER_QR_LOGIN, {query});
  },
  checkLoginQrStatus(query) {
    return http.get(API_URLS.USER_QR_CHECK, {query});
  },
  confirmLoginQr(params, LG) {
    return http.post(API_URLS.USER_QR_CONFIRM_LOGIN, {params, fail: (args) => ipRestrictedFail(args, LG)});
  },
  getUserProfile() {
    return http.get(API_URLS.USER_PROFILE_META, {});
  },
  telegramStatus(query) {
    return http.get(API_URLS.TELEGRAM_BIND_STATUS, {query});
  },
  telegramBind(params) {
    return http.post(API_URLS.TELEGRAM_BIND, {params});
  },
  telegramUnbind(params) {
    return http.post(API_URLS.TELEGRAM_UNBIND, {params});
  },
  telegramBotInfo(query) {
    return http.get(API_URLS.TELEGRAM_BOT_INFO, {query});
  },
  getCountryByIp() {
    return http.get(API_URLS.USER_COUNTRY_BY_IP, {});
  },
  saveReferralCode({params, query}) {
    return http.post(API_URLS.SAVE_REFERRAL_CODE, {params, query});
  },
};

function trackGeoData(data) {
  if (!data) {
    return;
  }
  if (data.code !== 0) {
    sd.track('geo_info', {
      code: data.code,
      msg: data.msg,
      country: data.country,
      countryCode: data.countryCode,
      city: data.city,
    });
  }
}
